import type {
  CharacteristicFilterWithRange,
  StoreShortWithSupplierType,
} from '@/types'

/**
 * Тело `filter`/`ranges` для POST-поиска (записка §10): бэкенд убрал
 * биндинг `filter[...]` из GET-запросов, характеристики теперь передаются
 * JSON-объектом `{ "<code>": ["1:32", ...] }` вместо повторяющихся query-ключей.
 */
export function buildCharacteristicFilterBody(
  filters: CharacteristicFilterWithRange[]
): {
  filter: Record<string, string[]>
  ranges: Record<string, { min?: number; max?: number }>
} {
  const filter: Record<string, string[]> = {}
  const ranges: Record<string, { min?: number; max?: number }> = {}

  filters.forEach((item) => {
    const values = new Set<string>()

    item.options?.forEach((option) => values.add(String(option.value)))
    item.chosenOptions?.forEach((option) => values.add(String(option)))

    if (values.size > 0) {
      filter[String(item.code)] = Array.from(values)
    }

    if (item.type === 'NUMERIC' && item.chosenRange) {
      const range: { min?: number; max?: number } = {}
      if (item.chosenRange.min !== undefined && item.chosenRange.min !== null) {
        range.min = item.chosenRange.min
      }
      if (item.chosenRange.max !== undefined && item.chosenRange.max !== null) {
        range.max = item.chosenRange.max
      }
      if (Object.keys(range).length > 0) {
        ranges[String(item.code)] = range
      }
    }
  })

  return { filter, ranges }
}

/** Тело `stores` для POST-поиска: `StoreFilterRequest` (`include`/`exclude`/`priority`). */
export function buildStoreFilterBody(stores: StoreShortWithSupplierType[]): {
  include: Record<string, string>
  exclude: Record<string, string>
  priority: Record<string, unknown>
} {
  const groupedBySupplier = stores.reduce(
    (acc, store) => {
      if (!acc[store.supplierId]) acc[store.supplierId] = new Set<number>()
      acc[store.supplierId].add(store.code)
      return acc
    },
    {} as { [supplierId: string]: Set<number> }
  )

  const include: Record<string, string> = {}
  Object.entries(groupedBySupplier).forEach(([supplierId, codes]) => {
    include[supplierId] = Array.from(codes).join(',')
  })

  return { include, exclude: {}, priority: {} }
}

export function buildStoresRequestPart(
  stores: StoreShortWithSupplierType[] | null | undefined
): {
  stores?: ReturnType<typeof buildStoreFilterBody>
} {
  return stores && stores.length > 0
    ? { stores: buildStoreFilterBody(stores) }
    : {}
}

const SEARCH_REQUEST_DEFAULTS: Record<string, unknown> = {
  page: 0,
  exactMatch: false,
  withDiscontinued: false,
  matchUniformSlottedCharacteristics: false,
}

export function omitSearchRequestDefaults<T extends Record<string, unknown>>(
  body: T
): Partial<T> {
  return Object.fromEntries(
    Object.entries(body).filter(([key, value]) => {
      if (value === undefined || value === null || value === '') return false
      if (
        key in SEARCH_REQUEST_DEFAULTS &&
        SEARCH_REQUEST_DEFAULTS[key] === value
      )
        return false
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'object') return Object.keys(value).length > 0
      return true
    })
  ) as Partial<T>
}
